import { IosShare } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";

import { tableInCardSx } from "@/components/common/unifiedTableSx";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import PlaceholderImage from "@/components/common/PlaceholderImage/PlaceholderImage";
import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import { Store } from "@/services/features/StoreService";
import {
  CurrencyCode,
  formatCurrencyCSV,
  formatCurrencyInStore,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import {
  formatStoreDateTimeCsv,
  formatStoreDateTimeFileStamp,
  formatStoreDateTimeTwoLine,
  getBrowserTimezone,
} from "@/utils/storeTime";

type Order = "asc" | "desc";

export interface OccurrenceTableRowBase {
  id: number;
  time: string;
  storeId: number;
  imageUrl: string;
  productName: string;
  sku: string;
  size: string;
  color: string;
  storeName: string;
  storeNumber: string;
  searchTimeSeconds: number;
  value?: number;
  createdAt: number;
}

interface OccurrenceImageCellProps<Row extends OccurrenceTableRowBase> {
  occurrence: Row;
  onRequestImage?: (occurrence: Row) => void;
}

const OccurrenceImageCell = <Row extends OccurrenceTableRowBase>({
  occurrence,
  onRequestImage,
}: OccurrenceImageCellProps<Row>) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "200px 0px",
    threshold: 0,
  });
  const hasRequestedRef = React.useRef(false);

  React.useEffect(() => {
    hasRequestedRef.current = false;
  }, [occurrence.id]);

  React.useEffect(() => {
    if (!inView || occurrence.imageUrl || hasRequestedRef.current) {
      return;
    }

    hasRequestedRef.current = true;
    onRequestImage?.(occurrence);
  }, [inView, occurrence, onRequestImage]);

  return (
    <Box
      ref={ref}
      className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center"
    >
      <PlaceholderImage
        alt={occurrence.productName}
        className="w-full h-full"
        imageLoading="lazy"
        imageUrl={occurrence.imageUrl}
      />
    </Box>
  );
};

interface OccurrenceColumnContext<Row extends OccurrenceTableRowBase> {
  currentLanguage: string;
  storeTimezones: Record<number, string>;
  formatOccurrenceAmount: (value: number, storeId: number) => string;
  formatOccurrenceAmountCsv: (value: number, storeId: number) => string;
  renderImageCell: (row: Row) => React.ReactNode;
}

export interface OccurrenceColumn<Row extends OccurrenceTableRowBase> {
  id: string;
  label: string;
  sortKey?: keyof Row;
  sortAccessor?: (row: Row) => string | number | null | undefined;
  align?: "left" | "right" | "center";
  headerClassName?: string;
  cellClassName?: string;
  headerSx?: object;
  cellSx?: object;
  renderCell: (row: Row, context: OccurrenceColumnContext<Row>) => React.ReactNode;
  getCsvValue?: (row: Row, context: OccurrenceColumnContext<Row>) => string | number;
}

interface OccurrencesTableBaseProps<Row extends OccurrenceTableRowBase> {
  title: string;
  data: Row[];
  loading: boolean;
  error: string | null;
  storeTimezones: Record<number, string>;
  columns: OccurrenceColumn<Row>[];
  emptyMessage: string;
  emptyState?: React.ReactNode;
  loadingCallout?: React.ReactNode;
  loadingState?: React.ReactNode;
  exportFilePrefix?: string;
  renderLeadingHeaderActions?: () => React.ReactNode;
  renderHeaderActions?: () => React.ReactNode;
  onRequestImage?: (occurrence: Row) => void;
  stores?: Store[];
  noDecimalAmounts?: boolean;
  paginate?: boolean;
  initialRowsPerPage?: number;
  rowsPerPageOptions?: number[];
}

const compareValues = (
  aValue: string | number | null | undefined,
  bValue: string | number | null | undefined,
  order: Order,
) => {
  const safeA = aValue ?? "";
  const safeB = bValue ?? "";

  if (safeB < safeA) {
    return order === "desc" ? -1 : 1;
  }
  if (safeB > safeA) {
    return order === "desc" ? 1 : -1;
  }
  return 0;
};

export const buildOccurrenceStoreLabel = (storeName: string, storeNumber: string) =>
  `${storeName} ${storeNumber ? `(${storeNumber})` : ""}`;

function OccurrencesTableBase<Row extends OccurrenceTableRowBase>({
  title,
  data,
  loading,
  error,
  storeTimezones,
  columns,
  emptyMessage,
  emptyState,
  loadingCallout,
  loadingState,
  exportFilePrefix = "occurrences_export",
  renderLeadingHeaderActions,
  renderHeaderActions,
  onRequestImage,
  stores = [],
  noDecimalAmounts = false,
  paginate = false,
  initialRowsPerPage = 25,
  rowsPerPageOptions = [25, 50, 100],
}: OccurrencesTableBaseProps<Row>) {
  const { t, i18n } = useTranslation();
  const [order, setOrder] = React.useState<Order>("desc");
  const initialSortKey = columns.find((column) => column.sortKey)?.sortKey ?? "createdAt";
  const [orderBy, setOrderBy] = React.useState<keyof Row>(initialSortKey as keyof Row);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);

  const storeById = React.useMemo(
    () => new Map(stores.map((store) => [store.id, store])),
    [stores],
  );
  const currentLocale = React.useMemo(
    () => new Intl.Locale(i18n.resolvedLanguage || i18n.language || "en-US"),
    [i18n.resolvedLanguage, i18n.language],
  );
  const currentLanguage = i18n.resolvedLanguage || i18n.language || "en-US";
  const browserTimezone = React.useMemo(() => getBrowserTimezone(), []);

  const formatOccurrenceAmount = React.useCallback(
    (value: number, storeId: number) => {
      const store = storeById.get(storeId);

      if (!noDecimalAmounts) {
        return formatCurrencyInStore(value, store, currentLocale);
      }

      const [rowCurrencyCode, rowCurrencySymbol] = getStoreCurrencyIdentifiers(
        store ? [store] : null,
      );

      return formatCurrencyWithNoDecimals(
        value,
        rowCurrencyCode as CurrencyCode,
        rowCurrencySymbol,
        currentLocale,
      );
    },
    [currentLocale, noDecimalAmounts, storeById],
  );
  const formatOccurrenceAmountCsv = React.useCallback(
    (value: number, storeId: number) => {
      const store = storeById.get(storeId);
      const [rowCurrencyCode, rowCurrencySymbol] = getStoreCurrencyIdentifiers(
        store ? [store] : null,
      );

      return formatCurrencyCSV(
        value,
        rowCurrencyCode as CurrencyCode,
        rowCurrencySymbol,
        currentLocale,
      );
    },
    [currentLocale, storeById],
  );

  const renderImageCell = React.useCallback(
    (row: Row) => <OccurrenceImageCell occurrence={row} onRequestImage={onRequestImage} />,
    [onRequestImage],
  );

  const columnContext = React.useMemo<OccurrenceColumnContext<Row>>(
    () => ({
      currentLanguage,
      storeTimezones,
      formatOccurrenceAmount,
      formatOccurrenceAmountCsv,
      renderImageCell,
    }),
    [
      currentLanguage,
      formatOccurrenceAmount,
      formatOccurrenceAmountCsv,
      renderImageCell,
      storeTimezones,
    ],
  );

  const handleRequestSort = (property: keyof Row) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const sortedData = React.useMemo(() => {
    const activeColumn = columns.find((column) => column.sortKey === orderBy);

    return [...data].sort((a, b) => {
      const aValue = activeColumn?.sortAccessor
        ? activeColumn.sortAccessor(a)
        : (a[orderBy] as string | number | null | undefined);
      const bValue = activeColumn?.sortAccessor
        ? activeColumn.sortAccessor(b)
        : (b[orderBy] as string | number | null | undefined);

      return compareValues(aValue, bValue, order);
    });
  }, [columns, data, order, orderBy]);
  const maxPage = Math.max(Math.ceil(sortedData.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, maxPage);
  const displayedData = paginate
    ? sortedData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
    : sortedData;
  const renderPagination = (compact = false) => (
    <LocalizedTablePagination
      component="div"
      count={sortedData.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      sx={compact ? { "& .MuiToolbar-root": { minHeight: 0, px: 0 } } : undefined}
    />
  );

  const handleExport = () => {
    if (data.length === 0) {
      return;
    }

    const csvColumns = columns.filter((column) => column.getCsvValue);
    const headers = csvColumns.map((column) => column.label);

    const csvRows = [
      headers.join(","),
      ...sortedData.map((row) =>
        csvColumns
          .map((column) => {
            const str = String(column.getCsvValue?.(row, columnContext) ?? "");
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(","),
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${exportFilePrefix}_${formatStoreDateTimeFileStamp(new Date(), browserTimezone)}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) {
    return (
      <Box className="bg-white p-6 rounded-2xl border border-red-50 shadow-md shadow-red-100 mt-6 text-center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="bg-white p-6 rounded-2xl border border-blue-50 shadow-md shadow-blue-100 mt-6">
      <Box className="flex justify-between items-center mb-6 gap-4">
        <Box className="flex items-center gap-4 min-w-0">
          <h2 className="text-[20px] font-semibold text-[#001F3F]">{title}</h2>
          {renderLeadingHeaderActions?.()}
        </Box>
        <Box className="flex items-center gap-4">
          {renderHeaderActions?.()}
          {paginate ? renderPagination(true) : null}
          <IconButton size="small" onClick={handleExport} disabled={loading || data.length === 0}>
            <IosShare
              fontSize="small"
              className={loading || data.length === 0 ? "text-gray-300" : "text-blue-500"}
            />
          </IconButton>
        </Box>
      </Box>

      {loading && loadingCallout ? <Box sx={{ mb: 2.5 }}>{loadingCallout}</Box> : null}
      <TableContainer>
        <Table sx={{ ...tableInCardSx, minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const isSortable = Boolean(column.sortKey);
                const alignClassName =
                  column.align === "right"
                    ? "text-right rtl:text-left"
                    : column.align === "center"
                      ? "text-center"
                      : "";

                return (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    className={`text-gray-500 border-none pb-4 ${alignClassName} ${column.headerClassName ?? ""}`.trim()}
                    sx={{ fontWeight: 500, ...(column.headerSx ?? {}) }}
                  >
                    {isSortable && column.sortKey ? (
                      <TableSortLabel
                        active={orderBy === column.sortKey}
                        direction={orderBy === column.sortKey ? order : "asc"}
                        onClick={() => handleRequestSort(column.sortKey!)}
                      >
                        {column.label}
                        {orderBy === column.sortKey ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? t("storeInsights.common.sortDirection.descending")
                              : t("storeInsights.common.sortDirection.ascending")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && loadingState ? (
              <TableRow className="table-empty-state">
                <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                  {loadingState}
                </TableCell>
              </TableRow>
            ) : loading ? (
              <TableSkeletonRows
                alignments={columns.map((column) => column.align)}
                columnCount={columns.length}
                imageColumns={columns.flatMap((column, index) =>
                  column.id === "image" ? [index] : [],
                )}
              />
            ) : displayedData.length > 0 ? (
              displayedData.map((row) => (
                <TableRow key={row.id} className="hover:bg-gray-50/50 transition-colors">
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      className={`py-4 border-gray-50 ${column.cellClassName ?? ""}`.trim()}
                      sx={column.cellSx}
                    >
                      {column.renderCell(row, columnContext)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="table-empty-state">
                <TableCell
                  colSpan={columns.length}
                  className="py-12 text-center text-gray-500 border-none"
                >
                  {emptyState ?? emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginate ? renderPagination() : null}
    </Box>
  );
}

export const defaultOccurrenceCsvTime = <Row extends OccurrenceTableRowBase>(
  row: Row,
  context: OccurrenceColumnContext<Row>,
) => formatStoreDateTimeCsv(row.time, context.storeTimezones[row.storeId]);

export const defaultOccurrenceTimeCell = <Row extends OccurrenceTableRowBase>(
  row: Row,
  context: OccurrenceColumnContext<Row>,
) => {
  const { line1, line2, timezoneLabel } = formatStoreDateTimeTwoLine(
    row.time,
    context.storeTimezones[row.storeId],
    context.currentLanguage,
  );

  return (
    <>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "#001F3F",
          lineHeight: 1.25,
        }}
      >
        {line1}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: "#6B7280",
          lineHeight: 1.25,
        }}
      >
        {line2}
      </Typography>
      {timezoneLabel ? (
        <Typography
          sx={{
            fontSize: 12,
            color: "#6B7280",
            lineHeight: 1.25,
          }}
        >
          {timezoneLabel}
        </Typography>
      ) : null}
    </>
  );
};

export default OccurrencesTableBase;
